// src/services/api/endpoints/users.ts
import { FabrikUser, DayPassResponse, CheckInResponse, MembershipTierResponse, UserNoteResponse, UserNoteCreate, UserNoteUpdate, FormAnswersResponse, SlackProfileResponse, ActionItem, UpdateUserProfilePayload} from '../../../types';


import { API_BASE_URL, fetchWithTokenRefresh } from '../client';
import { 
  generateCacheKey,
  getCacheItem,
  setCacheItem,
  invalidateCache
} from '../cache';
import { PaginatedResponse } from '../utils';
import { createLimiter } from '../utils/concurrencyLimit';

// Shared 3-wide queue for every outbound /users/search request.
// The admin dashboard's Users page fans out a prefetch across every page
// (userService.refreshAllData) and re-fires it after each search; with
// include_usage_data=true each request drives a heavy per-user N+1 on the
// backend that holds a SQLAlchemy connection for seconds. Funneling every
// caller through one 3-slot chokepoint bounds the in-flight burst regardless
// of how many fanout points run concurrently. Mirrors the /gatherings/search
// fix in ENG-933 — this is ENG-972.
const userSearchLimiter = createLimiter(3);

/**
 * Fetches users from the Fabrik API with optional filtering
 * @param token Firebase authentication token
 * @param page Page number (default: 1)
 * @param email Optional email filter
 * @param userId Optional user ID filter
 * @param includeUsageData Whether to include usage data (day passes, check-ins, membership info)
 * @param membersOnly Whether to filter to only users with active subscriptions
 * @param orderBy Field to sort by (default: 'last_name')
 * @param orderDirection Direction to sort (default: 'asc')
 * @param includeCaasData Whether to include CaaS team and client information
 * @param limit Number of users per page (default: 100, max: 100)
 * @returns Promise containing paginated users
 */
export const fetchUsers = async (
  token: string,
  page: number = 1,
  email?: string,
  userId?: string,
  includeUsageData: boolean = false,
  membersOnly: boolean = false,
  orderBy: string = 'last_name',
  orderDirection: 'asc' | 'desc' = 'asc',
  includeCaasData: boolean = true,
  limit: number = 100
): Promise<PaginatedResponse<FabrikUser>> => {
  try {
    const searchParams = new URLSearchParams();
    
    // Add pagination parameters
    searchParams.append('page', page.toString());
    searchParams.append('limit', limit.toString());
    
    // Add optional filters if provided
    if (email) {
      searchParams.append('email', email);
    }
    
    if (userId) {
      searchParams.append('user_id', userId);
    }
    
    // Add new parameters
    if (includeUsageData) {
      searchParams.append('include_usage_data', 'true');
    }
    
    if (membersOnly) {
      searchParams.append('members_only', 'true');
    }
    
    // Add sorting parameters
    searchParams.append('order_by', orderBy);
    searchParams.append('order_direction', orderDirection);
    
    // Add CaaS data parameter
    if (includeCaasData) {
      searchParams.append('include_caas_data', 'true');
    }
    
    const url = `${API_BASE_URL}/users/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, { page, limit, email, userId, includeUsageData, membersOnly, orderBy, orderDirection, includeCaasData });
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<PaginatedResponse<FabrikUser>>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data.
    // Routed through userSearchLimiter so concurrent /users/search calls never
    // exceed 3 in flight, regardless of caller (see ENG-972).
    const data = await userSearchLimiter(() => fetchWithTokenRefresh(url, token));
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Fetches a user by their ID
 * @param token Firebase authentication token
 * @param userId The ID of the user to fetch
 * @param bypassCache Optional flag to bypass cache and fetch fresh data
 * @returns Promise containing the user
 */
export const fetchUserById = async (token: string, userId: string, bypassCache: boolean = false): Promise<FabrikUser> => {
  try {
    const url = `${API_BASE_URL}/users/search?user_id=${userId}&include_caas_data=true`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response (unless bypassing cache)
    if (!bypassCache) {
      const cachedData = getCacheItem<FabrikUser>(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const response = await fetchWithTokenRefresh(url, token);
    
    // The search endpoint returns paginated results, so we need to extract the first user
    const user = response.items?.[0];
    if (!user) {
      throw new Error('User not found');
    }
    
    // Cache the response
    setCacheItem(cacheKey, user);
    
    return user;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Invalidates the users cache
 * Forces a fresh fetch on the next request
 */
export const invalidateUsersCache = (): void => {
  invalidateCache('users');
};

/**
 * Fetches day passes for a specific user
 * @param token Firebase authentication token
 * @param userId The user ID to fetch day passes for
 * @param onlyActive Whether to filter for only active day passes
 * @param bypassCache Whether to bypass cache and force fresh fetch
 * @returns Promise containing array of day passes
 */
export const fetchUserDayPasses = async (
  token: string,
  userId: string,
  onlyActive: boolean = false,
  bypassCache: boolean = false
): Promise<DayPassResponse[]> => {
  try {
    const searchParams = new URLSearchParams();
    if (onlyActive) {
      searchParams.append('only_active', 'true');
    }
    
    const url = `${API_BASE_URL}/day-passes/users/${userId}/day-passes${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const cacheKey = generateCacheKey(url, { userId, onlyActive });
    
    // Check if we have a valid cached response (unless bypassing cache)
    if (!bypassCache) {
      const cachedData = getCacheItem<DayPassResponse[]>(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response for 10 minutes (day passes change less frequently)
    setCacheItem(cacheKey, data, Date.now() + 10 * 60 * 1000);
    
    return data;
  } catch (error) {
    console.error('Error fetching user day passes:', error);
    throw error;
  }
};

/**
 * Fetches active day passes for a specific user
 * @param token Firebase authentication token
 * @param userId The user ID to fetch active day passes for
 * @param bypassCache Whether to bypass cache and force fresh fetch
 * @returns Promise containing array of active day passes
 */
export const fetchUserActiveDayPasses = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<DayPassResponse[]> => {
  return fetchUserDayPasses(token, userId, true, bypassCache);
};

/**
 * Invalidates the day passes cache for a specific user
 * @param userId The user ID to invalidate cache for
 */
export const invalidateUserDayPassesCache = (userId: string): void => {
  // Invalidate both active and all day passes cache
  const activeUrl = `${API_BASE_URL}/day-passes/users/${userId}/day-passes?only_active=true`;
  const allUrl = `${API_BASE_URL}/day-passes/users/${userId}/day-passes`;
  
  const activeCacheKey = generateCacheKey(activeUrl, { userId, onlyActive: true });
  const allCacheKey = generateCacheKey(allUrl, { userId, onlyActive: false });
  
  invalidateCache(activeCacheKey);
  invalidateCache(allCacheKey);
};

/**
 * Fetch recent check-ins for a specific user (admin endpoint)
 * @param token - Authentication token
 * @param userId - User ID to fetch check-ins for
 * @param bypassCache - Whether to bypass cache
 * @returns Promise with array of check-ins
 */
export const fetchUserRecentCheckIns = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<CheckInResponse[]> => {
  const cacheKey = `user-checkins-${userId}`;
  
  if (!bypassCache) {
    const cached = getCacheItem<CheckInResponse[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  try {
    const checkIns = await fetchWithTokenRefresh(`${API_BASE_URL}/check-in/admin/${userId}/recent`, token);
    
    // Cache for 5 minutes (shorter than day passes since check-ins change more frequently)
    setCacheItem(cacheKey, checkIns, 5 * 60 * 1000);
    
    return checkIns;
  } catch (error) {
    console.error(`Error fetching check-ins for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetch all membership tiers
 * @param token - Authentication token
 * @param bypassCache - Whether to bypass cache
 * @returns Promise with array of membership tiers
 */
export const fetchMembershipTiers = async (
  token: string,
  bypassCache: boolean = false
): Promise<MembershipTierResponse[]> => {
  const cacheKey = 'membership-tiers';
  
  if (!bypassCache) {
    const cached = getCacheItem<MembershipTierResponse[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const url = `${API_BASE_URL}/membership-tiers`;
  
  try {
    const tiers = await fetchWithTokenRefresh(url, token);
    
    // Cache for 30 minutes (tiers don't change often)
    setCacheItem(cacheKey, tiers, 30 * 60 * 1000);
    
    return tiers;
  } catch (error) {
    console.error('Error fetching membership tiers:', error);
    throw error;
  }
};

/**
 * Fetches all notes for a specific user
 * @param token Firebase authentication token
 * @param userId The user ID to fetch notes for
 * @param bypassCache Whether to bypass cache and force fresh fetch
 * @returns Promise containing array of user notes
 */
export const fetchUserNotes = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<UserNoteResponse[]> => {
  try {
    const url = `${API_BASE_URL}/user-notes/subjects/${userId}`;
    const cacheKey = generateCacheKey(url, { userId });
    
    // Check if we have a valid cached response (unless bypassing cache)
    if (!bypassCache) {
      const cachedData = getCacheItem<UserNoteResponse[]>(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response for 5 minutes (notes change less frequently than check-ins)
    setCacheItem(cacheKey, data, Date.now() + 5 * 60 * 1000);
    
    return data;
  } catch (error) {
    console.error('Error fetching user notes:', error);
    throw error;
  }
};

/**
 * Creates a new note for a user
 * @param token Firebase authentication token
 * @param payload Note creation payload
 * @returns Promise containing the created note
 */
export const createUserNote = async (
  token: string,
  payload: UserNoteCreate
): Promise<UserNoteResponse> => {
  try {
    const url = `${API_BASE_URL}/user-notes/`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    // Invalidate cache for this user's notes
    invalidateUserNotesCache(payload.subject_id);
    
    return data;
  } catch (error) {
    console.error('Error creating user note:', error);
    throw error;
  }
};

/**
 * Updates an existing note
 * @param token Firebase authentication token
 * @param noteId The ID of the note to update
 * @param payload Note update payload
 * @returns Promise containing the updated note
 */
export const updateUserNote = async (
  token: string,
  noteId: string,
  payload: UserNoteUpdate
): Promise<UserNoteResponse> => {
  try {
    const url = `${API_BASE_URL}/user-notes/${noteId}`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    // Invalidate cache - we'll need to know the subject_id to invalidate properly
    // For now, invalidate all user-notes caches
    invalidateCache('user-notes');
    
    return data;
  } catch (error) {
    console.error('Error updating user note:', error);
    throw error;
  }
};

/**
 * Deletes a note
 * @param token Firebase authentication token
 * @param noteId The ID of the note to delete
 * @returns Promise that resolves when deletion is complete
 */
export const deleteUserNote = async (
  token: string,
  noteId: string
): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/user-notes/${noteId}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'DELETE'
    });
    
    // Invalidate cache
    invalidateCache('user-notes');
  } catch (error) {
    console.error('Error deleting user note:', error);
    throw error;
  }
};

/**
 * Invalidates the notes cache for a specific user
 * @param userId The user ID to invalidate cache for
 */
export const invalidateUserNotesCache = (userId: string): void => {
  const url = `${API_BASE_URL}/user-notes/subjects/${userId}`;
  const cacheKey = generateCacheKey(url, { userId });
  invalidateCache(cacheKey);
};




/**
 * Fetches all form answers for a specific user (admin view)
 */
export const fetchUserFormAnswers = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<FormAnswersResponse> => {
  try {
    const url = `${API_BASE_URL}/forms/answers/admin/${userId}`;
    const cacheKey = generateCacheKey(url, { userId });

    if (!bypassCache) {
      const cachedData = getCacheItem<FormAnswersResponse>(cacheKey);
      if (cachedData) return cachedData;
    }

    const data = await fetchWithTokenRefresh(url, token);
    setCacheItem(cacheKey, data, Date.now() + 5 * 60 * 1000);
    return data;
  } catch (error) {
    console.error(`Error fetching form answers for ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetches all user notes for a specific user
 */
export const fetchUserNotesById = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<UserNoteResponse[]> => {
  try {
    const url = `${API_BASE_URL}/user-notes/subjects/${userId}`;
    const cacheKey = generateCacheKey(url, { userId });

    if (!bypassCache) {
      const cachedData = getCacheItem<UserNoteResponse[]>(cacheKey);
      if (cachedData) return cachedData;
    }

    const data = await fetchWithTokenRefresh(url, token);
    setCacheItem(cacheKey, data, Date.now() + 5 * 60 * 1000);
    return data;
  } catch (error) {
    console.error(`Error fetching user notes for ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetches the user's Slack profile
 */
export const fetchUserSlackProfile = async (
  token: string,
  userId: string,
  bypassCache: boolean = false
): Promise<SlackProfileResponse> => {
  try {
    const url = `${API_BASE_URL}/slack/profile?user_id=${encodeURIComponent(userId)}`;
    const cacheKey = generateCacheKey(url, { userId });

    if (!bypassCache) {
      const cachedData = getCacheItem<SlackProfileResponse>(cacheKey);
      if (cachedData) return cachedData;
    }

    const data = await fetchWithTokenRefresh(url, token);
    setCacheItem(cacheKey, data, Date.now() + 10 * 60 * 1000);
    return data;
  } catch (error) {
    console.error(`Error fetching Slack profile for user ${userId}:`, error);
    throw error;
  }
};


export const previewUserActionItems = async (
  token: string,
  userId: string
): Promise<{ items: { action: string; reasoning: string; task_type?: string }[] }> => {
  const url = `${API_BASE_URL}/users/${userId}/action-items/preview`;

  try {
    const data = await fetchWithTokenRefresh(url, token, {
      method: "POST",
    });

    return data; // { items: [...] }
  } catch (error) {
    console.error(`Error previewing action items for ${userId}:`, error);
    throw error;
  }
};


export const applyUserActionItems = async (
  token: string,
  userId: string,
  payload: { items: ActionItem[] }
): Promise<any> => {
  const url = `${API_BASE_URL}/users/${userId}/action-items/apply`;

  try {
    const data = await fetchWithTokenRefresh(url, token, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    return data;
  } catch (error) {
    console.error(`Error applying action items for ${userId}:`, error);
    throw error;
  }
};

/**
 * Sends a password reset email to a user (admin-only)
 * @param token Firebase authentication token
 * @param userId The user ID to send password reset email to
 * @returns Promise that resolves when email is sent
 */
export const sendPasswordResetEmail = async (
  token: string,
  userId: string
): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/users/${userId}/send-reset-password`;
    await fetchWithTokenRefresh(url, token, {
      method: 'POST'
    });
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

/**
 * Updates a user's email address (admin-only)
 * @param token Firebase authentication token
 * @param userId The user ID to update
 * @param newEmail The new email address
 * @returns Promise containing the updated user
 */
export const updateUserEmail = async (
  token: string,
  userId: string,
  newEmail: string
): Promise<FabrikUser> => {
  try {
    const url = `${API_BASE_URL}/users/${userId}/email`;
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      body: JSON.stringify({ new_email: newEmail })
    });
    
    // Invalidate cache for this user and the users list
    invalidateCache(`users/${userId}`);
    invalidateCache('users/search');
    
    return data;
  } catch (error) {
    console.error('Error updating user email:', error);
    throw error;
  }
};

/**
 * Updates a user's profile information (admin-only)
 * @param token Firebase authentication token
 * @param userId The user ID to update
 * @param payload Update payload containing fields to update
 * @returns Promise containing the updated user
 */
export const updateUserProfile = async (
  token: string,
  userId: string,
  payload: UpdateUserProfilePayload
): Promise<FabrikUser> => {
  try {
    const url = `${API_BASE_URL}/users/${userId}`;
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    // Invalidate cache for this user and the users list
    invalidateCache(`users/${userId}`);
    invalidateCache('users/search');
    
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Removes a user from all CaaS teams and resets their CaaS status (admin-only)
 * @param token Firebase authentication token
 * @param userId The user ID to remove from CaaS
 * @returns Promise containing the updated user
 */
export const removeCaasMembership = async (
  token: string,
  userId: string
): Promise<FabrikUser> => {
  try {
    const url = `${API_BASE_URL}/users/${userId}/caas-membership`;
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'DELETE'
    });
    
    // Invalidate cache for this user and the users list
    invalidateCache(`users/${userId}`);
    invalidateCache('users/search');
    
    return data;
  } catch (error) {
    console.error('Error removing CaaS membership:', error);
    throw error;
  }
};
